import type { BackendModule, ReadCallback } from 'i18next'

export const languages = [
  { shortcut: 'en', language: 'English', text: 'Which language do you speak?' },
  { shortcut: 'de', language: 'Deutsch', text: 'Welche Sprache sprichst du?' },
  { shortcut: 'pl', language: 'Polski', text: 'Jakim językiem mówisz?' },
  { shortcut: 'uk', language: 'Українська', text: 'Якою мовою ви говорите?' },
  { shortcut: 'fr', language: 'Français', text: 'Quelle langue parlez-vous?' },
  { shortcut: 'ar', language: 'العربية', text: 'ما هي اللغة التي تتحدثها؟' },
  { shortcut: 'fa', language: 'فارسی', text: 'به چه زبانی صحبت می‌کنید؟' },
  { shortcut: 'lt', language: 'lietuvių', text: 'Kokia kalba jūs kalbate?' },
  { shortcut: 'mi', language: 'Te Reo', text: 'He aha tō reo kōrero?' }
]

export const getLanguageName = (shortcut: string) => {
  return languages.find((lang) => lang.shortcut === shortcut)
}

// ---------------------------------------------------------------------------
// Lazy locale registry
// ---------------------------------------------------------------------------
//
// Every package that ships translations exports a `LocaleManifest` from
// `<pkg>/locales/manifest.ts`. A manifest is a *declaration* — it does
// not import any JSON at module load time. Each language is attached as
// a `() => import(...)` loader so that Vite / Rollup split the
// catalogues into separate chunks and only the active language is
// fetched at runtime.
//
// Apps compose one or more manifests into a `LocaleRegistry` and plug
// the resulting `BackendModule` into i18next. The registry is also the
// source of truth for "does this namespace exist?" checks (used when
// prefetching namespaces based on permissions, route changes, etc).
//
// Note: there is no English (`en`) catalogue by design — English strings
// live inline as `defaultValue` on every `t()` call (see
// `.cursor/rules/i18n/localization.mdc`). When a backend request comes
// in for `en` the registry returns `{}`, causing i18next to fall back
// to the provided `defaultValue`.

export type LocaleCatalogue = Record<string, unknown>

export type LocaleLoader = () => Promise<{ default: LocaleCatalogue }>

export interface LocaleManifest {
  /** i18next namespace this manifest contributes to. */
  namespace: string
  /**
   * Map from language code (e.g. 'de', 'fr') to a lazy loader that
   * resolves to the catalogue for that language. Must NOT contain an
   * `en` entry — English strings come from `defaultValue` in `t()`.
   */
  loaders: Record<string, LocaleLoader>
}

export interface LocaleRegistry {
  /** Add (or merge) a manifest after construction. */
  register(manifest: LocaleManifest): void
  /** True if the given namespace has at least one registered loader. */
  has(namespace: string): boolean
  /** All registered namespaces. */
  namespaces(): string[]
  /**
   * Resolve the catalogue for a namespace + language pair. Returns an
   * empty object for unknown namespaces and for languages with no
   * loader (e.g. English).
   */
  load(namespace: string, language: string): Promise<LocaleCatalogue>
}

const normalizeLanguage = (language: string) =>
  (language || '').split('-')[0] || ''

export function createLocaleRegistry(
  manifests: LocaleManifest[] = []
): LocaleRegistry {
  const store = new Map<string, Record<string, LocaleLoader>>()

  const register = (manifest: LocaleManifest) => {
    const existing = store.get(manifest.namespace) ?? {}
    store.set(manifest.namespace, { ...existing, ...manifest.loaders })
  }

  manifests.forEach(register)

  const has = (namespace: string) => store.has(namespace)

  const namespaces = () => Array.from(store.keys())

  const load = async (
    namespace: string,
    language: string
  ): Promise<LocaleCatalogue> => {
    const loaders = store.get(namespace)
    if (!loaders) return {}

    const lang = normalizeLanguage(language)
    const loader = loaders[lang]
    if (!loader) return {}

    const mod = await loader()
    return mod.default
  }

  return { register, has, namespaces, load }
}

/**
 * Build an i18next backend module from a `LocaleRegistry`. Attach with
 * `i18n.use(createI18nBackend(registry))` before calling `i18n.init()`.
 */
export function createI18nBackend(registry: LocaleRegistry): BackendModule {
  return {
    type: 'backend',
    init: () => {},
    read: (
      language: string,
      namespace: string,
      callback: ReadCallback
    ): void => {
      registry
        .load(namespace, language)
        .then((catalogue) => callback(null, catalogue))
        .catch((error) => callback(error as Error, false))
    }
  }
}
